import { useCallback } from 'react';
import type {
	CORE_PROJECT,
	SOFTWARE_PROJECT,
} from '@jira/platform__project-types/src/project-types.tsx';
import { metrics } from '@atlassian/browser-metrics';

import {
	SUGGESTED_COMMENT_AGENT_AI_FEATURE_PATH,
	SUGGESTED_COMMENT_AGENT_EXPERIENCE_ID,
	SuggestedCommentReplyAgentCommentType,
	type SuggestedCommentReplyAgentComment,
	type SuggestedCommentReplyCommentTypes,
} from '@atlassian/jira-ai-assistance-service-client/src/services/ai-features-client/suggested-comment-reply-agent/types.tsx';
import { invokeAIFeature } from '@atlassian/jira-ai-assistance-service-client/src/services/ai-features-client/index.tsx';

import { useExperienceFail } from '@atlassian/jira-experience-tracker/src/ui/experience-fail/index.tsx';
import { useExperienceStart } from '@atlassian/jira-experience-tracker/src/ui/experience-start/index.tsx';
import { useExperienceSuccess } from '@atlassian/jira-experience-tracker/src/ui/experience-success/index.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import { useNativeLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { getConvoAIProductReferrerKey } from '../../common/utils.tsx';
import {
	AI_COMMENTS_EXPERIENCE,
	AI_COMMENTS_HISTOGRAM,
	AI_COMMENTS_METRIC_KEY,
	FETCH_SUGGESTED_COMMENTS_FAILED_FLAG_ID,
	FETCH_SUGGESTED_COMMENTS_FAILED_FLAG,
} from './constants.tsx';
import type { FetchSuggestedComments, SuggestedComments, UseAIComments } from './types.tsx';

const transformComments: (
	comments?: SuggestedCommentReplyAgentComment[],
) => SuggestedComments | null = (comments) => {
	if (!comments) {
		return null;
	}
	const results: SuggestedComments = {};
	comments.forEach((comment) => {
		if (
			comment.commentType === SuggestedCommentReplyAgentCommentType.QUICK_REPLY ||
			comment.commentType === SuggestedCommentReplyAgentCommentType.QUESTION ||
			comment.commentType === SuggestedCommentReplyAgentCommentType.SUGGESTION ||
			comment.commentType === SuggestedCommentReplyAgentCommentType.JIRA_STATUS_UPDATE ||
			comment.commentType === SuggestedCommentReplyAgentCommentType.JIRA_COMMENT_REPLY ||
			comment.commentType === SuggestedCommentReplyAgentCommentType.JIRA_ASSIGNEE_MISSING ||
			comment.commentType === SuggestedCommentReplyAgentCommentType.JIRA_DESCRIPTION_MISSING ||
			comment.commentType === SuggestedCommentReplyAgentCommentType.JIRA_MOTIVATION
		) {
			results[comment.commentType] = comment.text;
		}
	});
	return results;
};

export const hasSuggestedComment = (
	selectedType: SuggestedCommentReplyAgentCommentType,
	suggestedComments: SuggestedComments | null,
): boolean => {
	if (!suggestedComments) {
		return false;
	}
	const comment = suggestedComments[selectedType];
	return !!comment;
};

const getSuggestedComments = async (
	issueKey: string,
	locale: string,
	projectType: typeof SOFTWARE_PROJECT | typeof CORE_PROJECT,
	commentTypes: SuggestedCommentReplyCommentTypes,
	stopMetric: () => void,
) => {
	const comments = await invokeAIFeature<typeof SUGGESTED_COMMENT_AGENT_EXPERIENCE_ID>(
		SUGGESTED_COMMENT_AGENT_EXPERIENCE_ID,
		{
			ai_feature_path: SUGGESTED_COMMENT_AGENT_AI_FEATURE_PATH,
			ai_feature_input: {
				object_id: issueKey,
				locale,
				product: getConvoAIProductReferrerKey(projectType),
				comment_type: commentTypes,
				adf_format: false,
			},
		},
	);

	stopMetric();

	const result = transformComments(comments?.ai_feature_output?.commentSuggestions);
	return result;
};

export const useAIComments: () => UseAIComments = () => {
	const locale = useNativeLocale();
	const startExperience = useExperienceStart(AI_COMMENTS_EXPERIENCE);
	const markExperienceSuccess = useExperienceSuccess(AI_COMMENTS_EXPERIENCE);
	const markExperienceFailed = useExperienceFail(AI_COMMENTS_EXPERIENCE);

	const aiCommentsMetric = metrics.interaction({
		key: AI_COMMENTS_METRIC_KEY,
		histogram: AI_COMMENTS_HISTOGRAM,
	});
	const { showFlag, dismissFlag, hasFlag } = useFlagsService();

	const fetchSuggestedComments: FetchSuggestedComments = useCallback(
		async ({ issueKey, projectType, selectedType, commentTypes, onFail }) => {
			startExperience();

			aiCommentsMetric.start();
			if (hasFlag?.(FETCH_SUGGESTED_COMMENTS_FAILED_FLAG_ID)) {
				dismissFlag(FETCH_SUGGESTED_COMMENTS_FAILED_FLAG_ID);
			}

			try {
				const suggestedComments = await getSuggestedComments(
					issueKey,
					locale,
					projectType,
					commentTypes,
					() => aiCommentsMetric.stop(),
				);

				if (hasSuggestedComment(selectedType, suggestedComments)) {
					markExperienceSuccess();
					return suggestedComments;
				}
				throw new Error('Selected comment type not returned');
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				aiCommentsMetric.stop();
				markExperienceFailed('use-ai-comment', error, {
					commentTypes: commentTypes?.toString() || '',
				});
				showFlag(FETCH_SUGGESTED_COMMENTS_FAILED_FLAG);
				onFail?.(error);
				return null;
			}
		},
		[
			aiCommentsMetric,
			dismissFlag,
			hasFlag,
			locale,
			markExperienceFailed,
			markExperienceSuccess,
			showFlag,
			startExperience,
		],
	);

	return { fetchSuggestedComments };
};
